import React, { useState, useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';
import { UserProfile, ThreadSummary } from '../types/user';
import { MyReviewDto } from '../api/review';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Toast from '../components/Toast';
import BasicInformationSection from '../components/BasicInformationSection';
import MessageHistorySection from '../components/MessageHistorySection';
import MyRatingsReviewsSection from '../components/MyRatingsReviewsSection';
import DataExportSection from '../components/DataExportSection';
import DeleteAccountSection from '../components/DeleteAccountSection';
import MarketingPreferencesSection from '../components/MarketingPreferencesSection';
import { fetchUserProfile, updateUserProfile } from '../api/userProfile';
import { getConversations } from '../api/message';
import { getMyReviews } from '../api/review';

export async function userProfileLoader() {
  const [userProfile, conversations, reviews] = await Promise.all([
    fetchUserProfile().catch(() => null),
    getConversations().catch(() => []),
    getMyReviews().catch(() => []),
  ]);
  return { userProfile, conversations, reviews };
}

export async function userProfileUpdateAction() {
  return null;
}

const UserProfilePage: React.FC = () => {
  const { userProfile, conversations, reviews: initialReviews } = useLoaderData() as {
    userProfile: UserProfile | null;
    conversations: ThreadSummary[];
    reviews: MyReviewDto[];
  };

  const [firstName, setFirstName] = useState(userProfile?.firstName ?? '');
  const [lastName, setLastName] = useState(userProfile?.lastName ?? '');
  const [phone, setPhone] = useState(userProfile?.phoneNumber ?? '');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [reviews, setReviews] = useState<MyReviewDto[]>(initialReviews);

  const dismissToast = useCallback(() => setToast(null), []);

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await updateUserProfile({ firstName, lastName, phoneNumber: phone });
      setToast({ message: 'Profile saved.', type: 'success' });
    } catch {
      setToast({ message: 'Save failed. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReviewDeleted = (reviewId: number) => {
    setReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  const handleReviewUpdated = (updated: MyReviewDto) => {
    setReviews(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface pb-24 md:pb-0">
      <TopAppBar />

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}

      <main className="max-w-2xl mx-auto px-container-margin py-8 md:pt-28 space-y-0">
        <h1 className="font-headline-lg text-on-surface mb-6">My Profile</h1>

        <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant divide-y divide-outline-variant overflow-hidden">
          <BasicInformationSection
            firstName={firstName}
            lastName={lastName}
            email={userProfile?.email ?? ''}
            phone={phone}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onPhoneChange={setPhone}
            onSaveChanges={handleSaveChanges}
            saving={saving}
          />
          <MessageHistorySection conversations={conversations} />
          <MyRatingsReviewsSection
            reviews={reviews}
            onReviewDeleted={handleReviewDeleted}
            onReviewUpdated={handleReviewUpdated}
          />
        </div>

        <MarketingPreferencesSection />
        <DataExportSection />
        <DeleteAccountSection />
      </main>

      <BottomNavBar />
    </div>
  );
};

export default UserProfilePage;
